import type { SupabaseClient } from '../../lib/supabase';
import type { NotificationEvent } from '../notifications/types';
import type { NotificationPreference, WorkspaceSettings } from './settingsLens';

/**
 * Reading and writing settings.
 *
 * Nothing here filters by user_id, and that is deliberate rather than an
 * oversight: every policy on user_notification_preferences is
 * `user_id = auth.uid()`, so the database returns one person's rows. A
 * client-side `.eq('user_id', me)` would read as the thing providing the
 * isolation, and the day somebody removed it nothing would look wrong.
 *
 * user_id IS set on insert, because the policy's WITH CHECK demands it match
 * the caller — a row without it is refused rather than silently misfiled.
 */

// One literal, unwrapped (PostgREST type inference).
// prettier-ignore
const PREFERENCE_COLUMNS = 'event_type, slack_enabled, in_app_alert_enabled';
// prettier-ignore
const WORKSPACE_COLUMNS = 'default_project_visibility, default_task_due_days, default_sample_due_days, require_due_date_on_tasks, allow_member_project_creation';

function fail(message: string): never {
  throw new Error(message);
}

export const settingsKeys = {
  all: ['settings'] as const,
  preferences: () => [...settingsKeys.all, 'notification-preferences'] as const,
  workspace: () => [...settingsKeys.all, 'workspace'] as const,
};

export async function fetchNotificationPreferences(
  client: SupabaseClient,
): Promise<NotificationPreference[]> {
  const { data, error } = await client
    .from('user_notification_preferences')
    .select(PREFERENCE_COLUMNS);
  if (error) {
    fail(error.message);
  }
  return data ?? [];
}

/**
 * Save one toggle.
 *
 * An upsert rather than an update, because the common case is the FIRST time
 * anybody touches a given event — there is no row until then (absent means
 * enabled). An update would silently affect nothing and the switch would flip
 * back on the next refetch.
 */
export async function saveNotificationPreference(
  client: SupabaseClient,
  input: {
    userId: string;
    eventType: NotificationEvent;
    slackEnabled: boolean;
    inAppAlertEnabled: boolean;
  },
): Promise<void> {
  const { error } = await client.from('user_notification_preferences').upsert(
    {
      user_id: input.userId,
      event_type: input.eventType,
      slack_enabled: input.slackEnabled,
      in_app_alert_enabled: input.inAppAlertEnabled,
    },
    { onConflict: 'user_id,event_type' },
  );
  if (error) {
    fail(error.message);
  }
}

export async function fetchWorkspaceSettings(
  client: SupabaseClient,
): Promise<WorkspaceSettings | null> {
  const { data, error } = await client
    .from('workspace_settings')
    .select(WORKSPACE_COLUMNS)
    .maybeSingle();
  if (error) {
    fail(error.message);
  }
  return data ?? null;
}

/**
 * Change a workspace default.
 *
 * `.eq('id', true)` names the singleton explicitly. Without a WHERE clause
 * PostgREST refuses a bulk update outright, and being explicit costs nothing
 * — the check constraint is still what guarantees there is only one row.
 *
 * Only a TAP admin's write survives — workspace_settings_admin_update. A
 * member's update matches no row and quietly changes nothing, which is why the
 * page hides the controls rather than relying on the refusal.
 */
export async function saveWorkspaceSettings(
  client: SupabaseClient,
  patch: Partial<WorkspaceSettings>,
): Promise<void> {
  // Built explicitly rather than spread. The generated Update type rejects
  // excess properties, which is the check that catches a column renamed in a
  // migration and missed here — and under exactOptionalPropertyTypes a
  // Partial<> carries `| undefined` on every key, which that check refuses.
  const changes: {
    default_project_visibility?: 'members_only' | 'all_tap';
    default_task_due_days?: number;
    default_sample_due_days?: number;
    require_due_date_on_tasks?: boolean;
    allow_member_project_creation?: boolean;
  } = {};
  if (patch.default_project_visibility !== undefined) {
    changes.default_project_visibility =
      patch.default_project_visibility === 'all_tap' ? 'all_tap' : 'members_only';
  }
  if (patch.default_task_due_days !== undefined) {
    changes.default_task_due_days = patch.default_task_due_days;
  }
  if (patch.default_sample_due_days !== undefined) {
    changes.default_sample_due_days = patch.default_sample_due_days;
  }
  if (patch.require_due_date_on_tasks !== undefined) {
    changes.require_due_date_on_tasks = patch.require_due_date_on_tasks;
  }
  if (patch.allow_member_project_creation !== undefined) {
    changes.allow_member_project_creation = patch.allow_member_project_creation;
  }
  const { error } = await client.from('workspace_settings').update(changes).eq('id', true);
  if (error) {
    fail(error.message);
  }
}

/** A person's own profile fields. The column grant is the field allowlist. */
export async function saveProfileSettings(
  client: SupabaseClient,
  input: {
    userId: string;
    displayName?: string;
    timeZone?: string;
    shiftStartHour?: number;
    shiftLengthHours?: number;
  },
): Promise<void> {
  const patch: {
    display_name?: string;
    time_zone?: string;
    shift_start_hour?: number;
    shift_length_hours?: number;
  } = {};
  if (input.displayName !== undefined) patch.display_name = input.displayName.trim();
  if (input.timeZone !== undefined) patch.time_zone = input.timeZone;
  if (input.shiftStartHour !== undefined) patch.shift_start_hour = input.shiftStartHour;
  if (input.shiftLengthHours !== undefined) patch.shift_length_hours = input.shiftLengthHours;
  if (Object.keys(patch).length === 0) {
    return;
  }
  const { error } = await client.from('profiles').update(patch).eq('id', input.userId);
  if (error) {
    fail(error.message);
  }
}
